// CodeMirror theme for every editor (SQL + DSL), driven entirely by the app's
// CSS tokens — the chrome by the surface/text/accent tokens, the syntax colours
// by each theme's --syn-* palette (global.css). One definition therefore tracks
// all app themes; only the `dark` flag (CodeMirror's own light/dark defaults for
// panels and lint markers) has to follow the theme's base.
import { EditorView } from '@codemirror/view';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { tags as t } from '@lezer/highlight';

const chrome = {
  '&': { color: 'var(--fg)', backgroundColor: 'var(--bg-elev-1)' },
  '.cm-content': { caretColor: 'var(--accent)' },
  '.cm-cursor, .cm-dropCursor': { borderLeftColor: 'var(--accent)', borderLeftWidth: '2px' },
  '.cm-gutters': { backgroundColor: 'var(--bg-elev-1)', color: 'var(--fg-dim)', border: 'none' },
  '.cm-activeLine': { backgroundColor: 'color-mix(in srgb, var(--fg) 4%, transparent)' },
  '.cm-activeLineGutter': { backgroundColor: 'transparent', color: 'var(--fg-muted)' },
  '.cm-selectionBackground, .cm-content ::selection': { backgroundColor: 'var(--cm-sel)' },
  '.cm-selectionMatch': { backgroundColor: 'color-mix(in srgb, var(--accent) 14%, transparent)' },
  '.cm-searchMatch': {
    backgroundColor: 'color-mix(in srgb, var(--find-hl) 28%, transparent)',
    outline: '1px solid color-mix(in srgb, var(--find-hl) 55%, transparent)',
  },
  '.cm-searchMatch.cm-searchMatch-selected': { backgroundColor: 'color-mix(in srgb, var(--find-hl) 50%, transparent)' },
  '.cm-foldPlaceholder': { backgroundColor: 'var(--bg-elev-3)', border: 'none', color: 'var(--fg-muted)', borderRadius: '4px', padding: '0 4px' },
  '.cm-tooltip': {
    backgroundColor: 'var(--bg-elev-1)', color: 'var(--fg)',
    border: '1px solid var(--border)', borderRadius: '8px', boxShadow: 'var(--shadow-2)',
  },
  '.cm-tooltip .cm-tooltip-arrow:before': { borderTopColor: 'var(--border)', borderBottomColor: 'var(--border)' },
  '.cm-tooltip .cm-tooltip-arrow:after': { borderTopColor: 'var(--bg-elev-1)', borderBottomColor: 'var(--bg-elev-1)' },
  '.cm-tooltip-autocomplete ul li[aria-selected]': { backgroundColor: 'var(--accent-soft-2)', color: 'var(--fg)' },
  '.cm-panels': { backgroundColor: 'var(--bg-elev-1)', color: 'var(--fg)' },
  '.cm-panels.cm-panels-top': { borderBottom: '1px solid var(--border-soft)' },
  '.cm-panels.cm-panels-bottom': { borderTop: '1px solid var(--border-soft)' },
};

const lightChrome = EditorView.theme(chrome, { dark: false });
const darkChrome = EditorView.theme(chrome, { dark: true });

const highlight = HighlightStyle.define([
  { tag: [t.keyword, t.operatorKeyword, t.controlKeyword, t.definitionKeyword, t.moduleKeyword, t.modifier],
    color: 'var(--syn-keyword)' },
  { tag: [t.string, t.regexp, t.character, t.docString], color: 'var(--syn-string)' },
  { tag: [t.number, t.integer, t.float, t.bool, t.null, t.atom, t.unit], color: 'var(--syn-number)' },
  { tag: [t.comment, t.lineComment, t.blockComment, t.docComment, t.meta], color: 'var(--syn-comment)', fontStyle: 'italic' },
  { tag: [t.typeName, t.className, t.namespace, t.tagName], color: 'var(--syn-type)' },
  { tag: [t.function(t.variableName), t.function(t.propertyName), t.standard(t.name), t.standard(t.variableName), t.macroName],
    color: 'var(--syn-fn)' },
  { tag: [t.special(t.name), t.special(t.variableName), t.self, t.labelName], color: 'var(--syn-var)' },
  { tag: [t.operator, t.derefOperator, t.compareOperator, t.arithmeticOperator, t.logicOperator, t.bitwiseOperator],
    color: 'var(--syn-op)' },
  { tag: [t.punctuation, t.separator, t.bracket, t.paren, t.brace, t.squareBracket], color: 'var(--fg-muted)' },
  // Back-quoted identifiers are names, not strings.
  { tag: t.special(t.string), color: 'var(--fg)' },
  { tag: [t.propertyName, t.attributeName], color: 'var(--syn-fn)' },
  { tag: t.invalid, color: 'var(--danger)' },
  { tag: t.link, textDecoration: 'underline' },
  { tag: t.heading, fontWeight: '600' },
  { tag: t.emphasis, fontStyle: 'italic' },
  { tag: t.strong, fontWeight: '600' },
]);

const syntax = syntaxHighlighting(highlight);
const LIGHT = [lightChrome, syntax];
const DARK = [darkChrome, syntax];

// The editor theme extension for a theme base ('dark' | 'light').
export const editorTheme = (base) => (base === 'light' ? LIGHT : DARK);
